import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid as MuiGrid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  Divider,
  IconButton,
  FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

import { EmployeeService } from '../../services/payrollService';
import { Employee, EmployeeStatus } from '../../types/payroll';
import { useRole } from '../../hooks/useRole';
import ConfirmDialog from '../common/ConfirmDialog';

// Define proper Grid types to fix TypeScript errors with container and item props
interface GridProps extends React.ComponentProps<typeof MuiGrid> {
  container?: boolean;
  item?: boolean;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

const Grid = styled(MuiGrid)<GridProps>(({ theme }) => ({}));

const EmployeeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAnyRole } = useRole();
  
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState<Partial<Employee>>({
    employee_no: '',
    first_name_en: '',
    last_name_en: '',
    full_name: '',
    status: 'active' as EmployeeStatus
  });
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  
  // Ensure user has permission
  const canManageEmployees = hasAnyRole(['hr', 'admin']);
  const isEditMode = !!id;
  
  // Load data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        
        // Fetch employee
        const response = await EmployeeService.getEmployeeById(Number(id));
        const employeeData = response.data;
        
        // Parse bank_info if it exists
        let bankAccountNo = '';
        let bankName = '';
        let accountName = '';
        
        if (employeeData.bank_info) {
          try {
            // Handle both string and object formats
            const bankInfo = typeof employeeData.bank_info === 'string' 
              ? JSON.parse(employeeData.bank_info) 
              : employeeData.bank_info;
              
            bankAccountNo = bankInfo.account_no || '';
            bankName = bankInfo.bank_name || '';
            accountName = bankInfo.account_name || '';
            
            console.log('Parsed bank info:', { bankAccountNo, bankName, accountName });
          } catch (e) {
            console.error('Error parsing bank_info:', e);
          }
        } else {
          // Fallback to legacy fields if they exist
          bankAccountNo = employeeData.bank_account_no || '';
          bankName = employeeData.bank_name || '';
        }
        
        setEmployee(employeeData);
        setFormData({
          employee_no: employeeData.employee_no,
          first_name_en: employeeData.first_name_en,
          last_name_en: employeeData.last_name_en,
          first_name_vi: employeeData.first_name_vi,
          last_name_vi: employeeData.last_name_vi,
          full_name: employeeData.full_name,
          job_title_id: employeeData.job_title_id,
          job_title: employeeData.job_title,
          status: employeeData.status,
          phone: employeeData.phone,
          email: employeeData.email,
          tax_code: employeeData.tax_code,
          si_code: employeeData.si_code,
          national_id: employeeData.national_id,
          dependants: employeeData.dependants,
          bank_account_no: bankAccountNo,
          bank_name: bankName,
          account_name: accountName
        });
        
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load employee data');
        console.error('Error fetching employee data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isEditMode]);
  
  // Handle field changes
  const handleChange = (field: keyof Employee, value: any) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Clear error for this field if exists
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: ''
      });
    }
    
    // Clear any success messages when form changes
    if (successMessage) {
      setSuccessMessage(null);
    }
  };
  
  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Required fields
    if (!formData.employee_no) {
      errors.employee_no = 'Employee number is required';
    }
    
    if (!formData.first_name_en) {
      errors.first_name_en = 'First name is required';
    }
    
    if (!formData.last_name_en) {
      errors.last_name_en = 'Last name is required';
    }
    
    // Optional fields - validate format if provided
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    if (formData.status && !['active', 'inactive'].includes(formData.status)) {
      errors.status = 'Status must be active or inactive';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Create bank_info JSON object
      const bankInfo = {
        account_no: formData.bank_account_no || '',
        bank_name: formData.bank_name || '',
        account_name: formData.account_name || ''
      };
      
      const payload: Partial<Employee> = {
        employee_no: formData.employee_no,
        first_name_en: formData.first_name_en,
        last_name_en: formData.last_name_en,
        first_name_vi: formData.first_name_vi,
        last_name_vi: formData.last_name_vi,
        full_name: formData.full_name,
        job_title_id: formData.job_title_id,
        job_title: formData.job_title,
        status: formData.status,
        phone: formData.phone,
        email: formData.email,
        tax_code: formData.tax_code,
        si_code: formData.si_code,
        national_id: formData.national_id,
        dependants: formData.dependants,
        bank_info: bankInfo, // Use the bank_info JSON object
        salary_profile: formData.salary_profile
      };
      
      console.log('Saving employee with bank_info:', bankInfo);
      
      if (isEditMode && id) {
        await EmployeeService.updateEmployee(Number(id), payload);
        setSuccessMessage('Employee updated successfully');
      } else {
        await EmployeeService.createEmployee(payload);
        setSuccessMessage('Employee created successfully');
      }
      
      // If successful, update the original employee data to match current form data
      // This prevents the "unsaved changes" warning when navigating away
      if (formData.employee_no && formData.first_name_en && formData.last_name_en && formData.status) {
        setEmployee(formData as Employee);
      }
      
      // Navigate back to employees list after short delay
      setTimeout(() => {
        navigate('/payroll/employees');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save employee data');
      console.error('Error saving employee data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle back
  const handleBack = () => {
    // If form has changes, confirm before navigating away
    if (isFormChanged()) {
      setConfirmDialogOpen(true);
    } else {
      navigateBack();
    }
  };
  
  // Check if form has been modified
  const isFormChanged = (): boolean => {
    if (!employee) return true; // New employee
    
    // Get bank info from employee
    let originalBankAccountNo = '';
    let originalBankName = '';
    let originalAccountName = '';
    
    if (employee.bank_info) {
      try {
        // Handle both string and object formats
        const bankInfo = typeof employee.bank_info === 'string' 
          ? JSON.parse(employee.bank_info) 
          : employee.bank_info;
          
        originalBankAccountNo = bankInfo.account_no || '';
        originalBankName = bankInfo.bank_name || '';
        originalAccountName = bankInfo.account_name || '';
      } catch (e) {
        console.error('Error parsing bank_info in isFormChanged:', e);
      }
    } else {
      // Fallback to legacy fields if they exist
      originalBankAccountNo = employee.bank_account_no || '';
      originalBankName = employee.bank_name || '';
    }
    
    // Compare current form data with original employee data
    return (
      formData.employee_no !== employee.employee_no ||
      formData.first_name_en !== employee.first_name_en ||
      formData.last_name_en !== employee.last_name_en ||
      formData.first_name_vi !== employee.first_name_vi ||
      formData.last_name_vi !== employee.last_name_vi ||
      formData.full_name !== employee.full_name ||
      formData.job_title_id !== employee.job_title_id ||
      formData.status !== employee.status ||
      formData.phone !== employee.phone ||
      formData.email !== employee.email ||
      formData.tax_code !== employee.tax_code ||
      formData.si_code !== employee.si_code ||
      formData.national_id !== employee.national_id ||
      formData.dependants !== employee.dependants ||
      formData.bank_account_no !== originalBankAccountNo ||
      formData.bank_name !== originalBankName ||
      formData.account_name !== originalAccountName ||
      JSON.stringify(formData.salary_profile) !== JSON.stringify(employee.salary_profile)
    );
  };
  
  // Navigate back
  const navigateBack = () => {
    if (isEditMode) {
      navigate(`/payroll/employees/${id}`);
    } else {
      navigate('/payroll/employees');
    }
  };
  
  // Handle confirm dialog actions
  const handleConfirmDiscard = () => {
    setConfirmDialogOpen(false);
    navigateBack();
  };
  
  const handleCancelDiscard = () => {
    setConfirmDialogOpen(false);
  };
  
  if (!canManageEmployees) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            You do not have permission to manage employees.
          </Typography>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/payroll/employees')} 
            sx={{ mt: 2 }}
          >
            Back to Employees
          </Button>
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={handleBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1">
              {isEditMode ? 'Edit Employee' : 'Add New Employee'}
            </Typography>
          </Box>
          
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            {isEditMode ? 'Update' : 'Create'} Employee
          </Button>
        </Box>
        
        {loading && <LinearProgress sx={{ mb: 3 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}
        
        <Box component="form">
          {/* Basic Info Section */}
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Employee Number"
                  value={formData.employee_no || ''}
                  onChange={(e) => handleChange('employee_no', e.target.value)}
                  fullWidth
                  required
                  error={!!formErrors.employee_no}
                  helperText={formErrors.employee_no || 'Required'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="First Name"
                  value={formData.first_name_en || ''}
                  onChange={(e) => handleChange('first_name_en', e.target.value)}
                  fullWidth
                  required
                  error={!!formErrors.first_name_en}
                  helperText={formErrors.first_name_en || 'Required'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Last Name"
                  value={formData.last_name_en || ''}
                  onChange={(e) => handleChange('last_name_en', e.target.value)}
                  fullWidth
                  required
                  error={!!formErrors.last_name_en}
                  helperText={formErrors.last_name_en || 'Required'}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Full Name"
                  value={formData.full_name || ''}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  fullWidth
                  helperText="Optional - for display purposes"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  fullWidth
                  error={!!formErrors.email}
                  helperText={formErrors.email || 'Optional'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  value={formData.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  fullWidth
                />
              </Grid>
              

              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name (Vietnamese)"
                  value={formData.first_name_vi || ''}
                  onChange={(e) => handleChange('first_name_vi', e.target.value)}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name (Vietnamese)"
                  value={formData.last_name_vi || ''}
                  onChange={(e) => handleChange('last_name_vi', e.target.value)}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={formData.status || 'active'}
                    label="Status"
                    onChange={(e) => handleChange('status', e.target.value)}
                    error={!!formErrors.status}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                  {formErrors.status ? (
                    <FormHelperText error>{formErrors.status}</FormHelperText>
                  ) : (
                    <FormHelperText>Defaults to Active</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          
          {/* Employment Section */}
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Employment Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>

              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Tax Code"
                  value={formData.tax_code || ''}
                  onChange={(e) => handleChange('tax_code', e.target.value)}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Social Insurance Code"
                  value={formData.si_code || ''}
                  onChange={(e) => handleChange('si_code', e.target.value)}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="National ID"
                  value={formData.national_id || ''}
                  onChange={(e) => handleChange('national_id', e.target.value)}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Number of Dependants"
                  value={formData.dependants || ''}
                  onChange={(e) => handleChange('dependants', parseInt(e.target.value) || 0)}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Job Title"
                  value={formData.job_title || ''}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                  fullWidth
                  helperText="Display name for job title"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Job Title ID"
                  value={formData.job_title_id || ''}
                  onChange={(e) => handleChange('job_title_id', parseInt(e.target.value) || undefined)}
                  fullWidth
                  error={!!formErrors.job_title_id}
                  helperText={formErrors.job_title_id || 'Optional - Reference ID for job title'}
                  InputProps={{
                    inputProps: { min: 1 }
                  }}
                />
              </Grid>
              

            </Grid>
          </Box>
          

          
          {/* Banking Section */}
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Banking Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bank Account Number"
                  value={formData.bank_account_no || ''}
                  onChange={(e) => handleChange('bank_account_no', e.target.value)}
                  fullWidth
                  error={!!formErrors.bank_account_no}
                  helperText={formErrors.bank_account_no || 'Optional'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.bank_name}>
                  <TextField
                    label="Bank Name"
                    value={formData.bank_name || ''}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    fullWidth
                    error={!!formErrors.bank_name}
                    helperText="Optional"
                  />
                  {formErrors.bank_name && (
                    <FormHelperText>{formErrors.bank_name}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Account Name"
                  value={formData.account_name || ''}
                  onChange={(e) => handleChange('account_name', e.target.value)}
                  fullWidth
                  helperText="Name of the account holder (Optional)"
                />
              </Grid>
            </Grid>
          </Box>
          
          {/* Notes Section */}
          <Box mb={4}>
            
          </Box>
          
          <Box display="flex" justifyContent="flex-end" mt={4}>
            <Button 
              variant="outlined" 
              onClick={handleBack} 
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSave}
              disabled={loading}
              startIcon={<SaveIcon />}
            >
              {isEditMode ? 'Update' : 'Create'} Employee
            </Button>
          </Box>
        </Box>
      </Paper>
      
      {/* Confirmation Dialog for Unsaved Changes */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Discard Changes"
        content="You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost."
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
        confirmText="Discard Changes"
        confirmColor="error"
      />
    </Container>
  );
};

export default EmployeeForm;
